import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import DatePicker from "src/components/calendar/DatePicker";
import Dropdown from "src/components/dropdowns/Dropdown";
import Input from "src/components/inputs/Input";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { GENDERS } from "src/utils/constants";
import { number, object, string } from "yup";
import { useGetMemberships } from "../../memberships/api-clients";
import { MembershipType } from "../../memberships/list/memberships-types";
import {
  useCreateClient,
  useGetClientData,
  useUpdateClient,
} from "../api-clients";

const FORM_FIELDS = {
  NAME: "name",
  EMAIL: "email",
  PHONE: "phone",
  ADDRESS: "address",
  DOB: "dob",
  GENDER: "gender",
  GST: "gst",
  MEMBERSHIP: "membership",
};

const CreateClient = ({ isInViewMode = false }: { isInViewMode?: boolean }) => {
  useSetActiveTab(TAB_NAMES.CLIENT_MANAGEMENT);
  const navigate = useNavigate();
  const { clientId: id } = useParams();

  const clientId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!clientId, [clientId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetClientLoading, data } = useGetClientData(clientId);

  const clientData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateClient,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateClient(clientId);

  const { isLoading: isGetMembershipLoading, data: membershipRes } =
    useGetMemberships({});

  const { isLoading, isSuccess, execute, error } = useCreateClient();

  const { values, errors, handleSubmit, setFieldValue, touched } = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: clientData?.[FORM_FIELDS.NAME] ?? "",
      [FORM_FIELDS.EMAIL]: clientData?.[FORM_FIELDS.EMAIL] ?? "",
      [FORM_FIELDS.PHONE]: clientData?.[FORM_FIELDS.PHONE] ?? "",
      [FORM_FIELDS.ADDRESS]: clientData?.[FORM_FIELDS.ADDRESS] ?? "",
      [FORM_FIELDS.DOB]: clientData?.[FORM_FIELDS.DOB] ?? null,
      [FORM_FIELDS.GENDER]: clientData?.[FORM_FIELDS.GENDER] ?? "",
      [FORM_FIELDS.GST]: clientData?.[FORM_FIELDS.GST] ?? "",
      [FORM_FIELDS.MEMBERSHIP]: clientData?.[FORM_FIELDS.MEMBERSHIP] ?? null,
    },
    validationSchema: object({
      [FORM_FIELDS.NAME]: string().required("This is a Required field."),
      [FORM_FIELDS.PHONE]: number().required("This is a Required field."),
      [FORM_FIELDS.EMAIL]: string()
        .email("Please enter valid email.")
        .required("This is a Required field."),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateClient(data);
        return;
      }
      execute(data);
    },
  });

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Client Updated" : "Client Created."
      );
      navigate("/store/clients");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetClientLoading || isUpdateLoading,
    [isGetClientLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  const membershipData = useMemo(() => {
    return (membershipRes?.data?.rows ?? []).map((item: MembershipType) => {
      return { label: item.name, value: item.id };
    });
  }, [membershipRes?.data]);

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Add Client" showBreadcrumb />
      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
          <Input
            label="Full Name"
            placeholder="Full Name"
            name={FORM_FIELDS.NAME}
            value={values[FORM_FIELDS.NAME] as string}
            error={getFieldError(FORM_FIELDS.NAME)}
            onChange={(e) => setFieldValue(FORM_FIELDS.NAME, e.target.value)}
            required
            disabled={isFieldDisabled}
          />
          <Input
            type="email"
            label="Email"
            placeholder="Email"
            name={FORM_FIELDS.EMAIL}
            value={values[FORM_FIELDS.EMAIL] as string}
            error={getFieldError(FORM_FIELDS.EMAIL)}
            onChange={(e) => setFieldValue(FORM_FIELDS.EMAIL, e.target.value)}
            disabled={isFieldDisabled}
            required
          />
          <Input
            type="number"
            label="Phone Number"
            placeholder="Phone Number"
            name={FORM_FIELDS.PHONE}
            value={values[FORM_FIELDS.PHONE] as string}
            error={getFieldError(FORM_FIELDS.PHONE)}
            onChange={(e) => setFieldValue(FORM_FIELDS.PHONE, e.target.value)}
            disabled={isFieldDisabled}
            required
          />
          <Input
            label="Address"
            placeholder="Address"
            name={FORM_FIELDS.ADDRESS}
            value={values[FORM_FIELDS.ADDRESS] as string}
            error={getFieldError(FORM_FIELDS.ADDRESS)}
            onChange={(e) => setFieldValue(FORM_FIELDS.ADDRESS, e.target.value)}
            disabled={isFieldDisabled}
          />
          <DatePicker
            label="Date Of Birth (MM/DD/YY)"
            value={values[FORM_FIELDS.DOB]}
            onChange={(value) => setFieldValue(FORM_FIELDS.DOB, value)}
            disabled={isFieldDisabled}
          />
          <Dropdown
            label="Gender"
            placeholder="Select Gender"
            className={"w-full"}
            options={GENDERS}
            value={values[FORM_FIELDS.GENDER] as string}
            onChange={(val) => setFieldValue(FORM_FIELDS.GENDER, val)}
            disabled={isFieldDisabled}
          />
          <Input
            label="GST"
            placeholder="GST"
            name={FORM_FIELDS.GST}
            value={values[FORM_FIELDS.GST] as string}
            error={getFieldError(FORM_FIELDS.GST)}
            onChange={(e) => setFieldValue(FORM_FIELDS.GST, e.target.value)}
            disabled={isFieldDisabled}
          />
          <Dropdown
            label="Membership"
            placeholder="Select Membership"
            className={"w-full"}
            options={membershipData}
            value={values[FORM_FIELDS.MEMBERSHIP] as string}
            onChange={(val) => setFieldValue(FORM_FIELDS.MEMBERSHIP, val)}
            disabled={isFieldDisabled}
          />
        </div>
        <Button
          text={isLoading ? "Loading..." : "Submit"}
          onClick={handleSubmit}
          primary
          className="h-[40px] px-10"
          isDisabled={isFieldDisabled}
        />
      </div>
    </div>
  );
};

export default CreateClient;

const dummyMembershipOPtions = [
  {
    label: "Gold",
    value: 1,
  },
  {
    label: "Silver",
    value: 2,
  },
];
